import React, { forwardRef } from "react";
import { Message } from "../../interfaces/chat.interface";
import { ConversationType } from "../../constants/chat.constant";
import { Citation } from "../../constants/citation.constant";
import ChatInput, { ChatInputRef } from "./chat-input";
import { ChatMessages } from "./ChatMessages";
import { ChatSuggestions } from "./ChatSuggestions";
import { SourcesModal } from "../modal/sourcesModal";
import ChatHeader from "../header/chat-header";

/**
 * Props for the ChatContainer component
 */
interface ChatContainerProps {
  // State
  /** Messages to display in the conversation */
  displayConversation: Message[] | null;
  /** Whether a response is currently being generated */
  isResponseGenerating: boolean;
  /** Whether messages are being loaded */
  isLoading: boolean;
  /** ID of the conversation currently streaming */
  streamingConversationId: string | null;
  /** ID of the selected conversation */
  selectedConversationId?: string;
  /** Whether the sources modal is open */
  sourcesOpen: boolean;
  /** Index of the active message showing sources */
  activeMessageIndex: number | null;
  /** Current input content */
  content: string;
  /** Selected AI model */
  selectedModel: string;
  /** Model temperature setting */
  modelTemperature: number;
  /** Model reasoning effort setting */
  modelEffort: number;
  /** Whether conversation has messages */
  hasMessages: boolean;
  /** Whether to show upcoming changes */
  showUpcoming: boolean;
  /** Type of conversation */
  conversationType: ConversationType;
  /** Whether citation is loading */
  isCitationLoading: boolean;
  /** ID of the selected citation */
  selectedCitationId: number | null;

  // Actions
  /** Callback fired when sources are opened */
  onOpenSources: (sources: Citation[], messageIndex: number) => void;
  /** Callback fired when sources are closed */
  onCloseSources: () => void;
  /** Callback fired when citation details are opened */
  onOpenCitationDetails: (citation: Citation) => void;
  /** Callback fired when message is sent */
  onSend: (message: Message) => void;
  /** Callback fired when typing starts */
  onStartTyping: () => void;
  /** Callback fired when typing stops */
  onStopTyping: () => void;
  /** Callback fired when input changes */
  onInputChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  /** Callback fired on keyboard events */
  onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  /** Callback fired when model changes */
  onModelChange: (model: string) => void;
  /** Callback fired when temperature changes */
  onTemperatureChange: (temperature: number) => void;
  /** Callback fired when reasoning effort changes */
  onReasoningEffortChange: (effort: number) => void;
  /** Callback fired to start new chat */
  onNewChat: () => void;
  /** Callback to set show upcoming state */
  setShowUpcoming: (show: boolean) => void;
  /** Callback fired to cancel streaming */
  onCancelStream: () => void;

  // Data
  /** Desktop suggestion prompts */
  suggestions: Array<{ prompt: string }>;
  /** Mobile suggestion prompts */
  mobileSuggestions: Array<{ prompt: string }>;
  /** Citation sources */
  sources: Citation[];
  /** Citation details data */
  citationDetails: Citation | null;
  /** Whether citation details modal is open */
  citationDetailsOpen: boolean;
  /** Callback fired when citation details are closed */
  onCloseCitationDetails: () => void;

  // File persistence
  /** Function to get files for a conversation */
  getFilesForConversation?: (conversationId: string) => File[];
  /** Function to update files for a conversation */
  updateFilesForConversation?: (conversationId: string, files: File[]) => void;

  /** Whether sensitive chat mode is active */
  isSensitive?: boolean;
  /** Callback fired when sensitive mode is toggled */
  onToggleSensitive?: () => void;
  /** Whether sensitive mode toggle is disabled */
  isSensitiveDisabled?: boolean;
}

/**
 * ChatContainer component is the main container for the chat interface.
 * Conditionally renders ChatMessages with ChatInput when messages exist, or
 * ChatSuggestions when starting a new conversation. Manages sources sidebar.
 * 
 * @component
 * @param {ChatContainerProps} props - Component props
 * @returns {JSX.Element} Chat container with messages or suggestions
 * 
 * @example
 * ```tsx
 * <ChatContainer
 *   displayConversation={messages}
 *   hasMessages={true}
 *   onSend={handleSend}
 *   isResponseGenerating={isGenerating}
 * />
 * ```
 */
export const ChatContainer = forwardRef<ChatInputRef, ChatContainerProps>(({
  displayConversation,
  isResponseGenerating,
  isLoading,
  streamingConversationId,
  selectedConversationId,
  sourcesOpen,
  activeMessageIndex,
  content,
  selectedModel,
  modelTemperature,
  modelEffort,
  hasMessages,
  showUpcoming,
  conversationType,
  isCitationLoading,
  selectedCitationId,
  onOpenSources,
  onCloseSources,
  onOpenCitationDetails,
  onSend,
  onStartTyping,
  onStopTyping,
  onInputChange,
  onKeyDown,
  onModelChange,
  onTemperatureChange,
  onReasoningEffortChange,
  onNewChat,
  setShowUpcoming,
  suggestions,
  mobileSuggestions,
  sources,
  getFilesForConversation,
  updateFilesForConversation,
  isSensitive = false,
  onToggleSensitive,
  isSensitiveDisabled = false,
  onCancelStream
}, ref) => {
  return (
    <div className="flex h-full w-full overflow-hidden">
      {/* Main chat content */}
      <div className="flex flex-col h-full flex-1 min-w-0 overflow-hidden">
        <ChatHeader
          hasMessages={hasMessages}
          setShowUpcoming={setShowUpcoming}
          isSensitive={isSensitive}
          onToggleSensitive={onToggleSensitive}
          isSensitiveDisabled={isSensitiveDisabled}
          conversationType={conversationType}
        />

        {hasMessages ? (
          <>
            <ChatMessages
              displayConversation={displayConversation}
              isResponseGenerating={isResponseGenerating}
              isLoading={isLoading}
              streamingConversationId={streamingConversationId}
              selectedConversationId={selectedConversationId}
              sourcesOpen={sourcesOpen}
              activeMessageIndex={activeMessageIndex}
              onOpenSources={onOpenSources}
              onCloseSources={onCloseSources}
            />

            <div className="w-full px-4 md:z-[40]">
              <ChatInput
                ref={ref}
                activeConversation={displayConversation}
                content={content}
                onStartTyping={onStartTyping}
                onStopTyping={onStopTyping}
                onInputChange={onInputChange}
                onKeyDown={onKeyDown}
                onSend={onSend}
                onModelChange={onModelChange}
                temperature={modelTemperature}
                effort={modelEffort}
                onTemperatureChange={onTemperatureChange}
                onReasoningEffortChange={onReasoningEffortChange}
                isResponseGenerating={isResponseGenerating}
                selectedModel={selectedModel}
                selectedConversationId={selectedConversationId}
                getFilesForConversation={getFilesForConversation}
                updateFilesForConversation={updateFilesForConversation}
                onCancelStream={onCancelStream}
                conversationType={conversationType}

              />
            </div>
          </>
        ) : (
          <ChatSuggestions
            suggestions={suggestions}
            mobileSuggestions={mobileSuggestions}
            conversationType={conversationType}
            onSend={onSend}
            content={content}
            displayConversation={displayConversation}
            isResponseGenerating={isResponseGenerating}
            selectedModel={selectedModel}
            modelTemperature={modelTemperature}
            modelEffort={modelEffort}
            onModelChange={onModelChange}
            onTemperatureChange={onTemperatureChange}
            onReasoningEffortChange={onReasoningEffortChange}
            onStartTyping={onStartTyping}
            onStopTyping={onStopTyping}
            onInputChange={onInputChange}
            onKeyDown={onKeyDown}
            onSendMessage={onSend}
            isSensitive={isSensitive}
            onToggleSensitive={onToggleSensitive}
            isSensitiveDisabled={isSensitiveDisabled}
            onCancelStream={onCancelStream}
            chatInputRef={ref}
          />
        )}
      </div>

      {/* Sources Sidebar */}
      <SourcesModal
        open={sourcesOpen}
        sources={sources}
        onClose={onCloseSources}
        onSourceClick={onOpenCitationDetails}
        isCitationLoading={isCitationLoading}
        selectedCitationId={selectedCitationId}
      />
    </div>
  );
});

ChatContainer.displayName = "ChatContainer";
